import * as ko from 'knockout';

import i18n from '../i18n';
import { translate } from '../i18n_text';
import { CropVarietyData, ReplacedVarietyData } from '../api/crop_varieties';
import { DimensionMeta } from './dimension_meta';
import { Dimension } from './dimension';
import {
  getBreederSearchConfig,
  getCropVarietyTypeSearchConfig,
  getCropVarietySearchConfig,
} from '../components/configs/search_configs';
import { CropData } from '../api/crops';
import { BreederData } from '../api/organizations';
import { NameI18nData } from '../api/names';

export class CropVariety extends Dimension {
  crop = ko.observable<CropData>(null).extend({
    required: true,
  });
  cropEditable = ko.observable(true);
  origin = ko.observable<BreederData>(null);
  origin2 = ko.observable<BreederData>(null);
  varietyType = ko.observable<NameI18nData>(null);
  // Optional, like the payload they come from: the serializer derives each from its breeder, so
  // neither is there when that breeder is not.
  originType?: NameI18nData;
  origin2Type?: NameI18nData;
  parent1 = ko.observable<NameI18nData>(null);
  parent2 = ko.observable<NameI18nData>(null);

  replacing = ko.observableArray<ReplacedVarietyData>();
  replacedBy = ko.observable<ReplacedVarietyData>(null);
  replacingFilter = ko.observable('');
  selectedOther = ko.observable<CropVarietyData>(null);
  replaceMode = ko.observable<string>(null);

  editUrl = ko.pureComputed(() => {
    return '/crop_varieties/' + this.id() + '/';
  });

  originSearch = getBreederSearchConfig(this.origin);
  origin2Search = getBreederSearchConfig(this.origin2);
  varietyTypeSearch = getCropVarietyTypeSearchConfig(this.varietyType);
  parent1Search = getCropVarietySearchConfig(this.parent1);
  parent2Search = getCropVarietySearchConfig(this.parent2);
  selectedOtherSearch = getCropVarietySearchConfig(this.selectedOther, this.crop);

  isCanonical = ko.pureComputed(() => this.replacing().length > 0);
  isReplaced = ko.pureComputed(() => !!this.replacedBy());
  showsSearch = ko.pureComputed(() => this.replacing().length > 5);

  filteredReplacing = ko.pureComputed(() => {
    const filter = this.replacingFilter().trim().toLowerCase();
    if (!filter) {
      return this.replacing();
    }
    return this.replacing().filter(
      (duplicate) =>
        duplicate.name.toLowerCase().includes(filter) ||
        duplicate.code.toLowerCase().includes(filter)
    );
  });

  currentName = ko.pureComputed(() => translate(this.nameJson()));
  selectedOtherName = ko.pureComputed(() =>
    this.selectedOther() ? translate(this.selectedOther().name_json) : ''
  );
  keepCurrentLabel = ko.pureComputed(() =>
    i18n.t('Keep current variety and archive "{{ name }}"', { name: this.selectedOtherName() })()
  );
  keepSelectedLabel = ko.pureComputed(() =>
    i18n.t('Keep selected variety and archive "{{ name }}"', { name: this.currentName() })()
  );

  canReplace = ko.pureComputed(
    () => !!this.selectedOther() && (this.isCanonical() || !!this.replaceMode())
  );

  constructor(data?: CropVarietyData, dimensionMeta?: DimensionMeta) {
    super(data, dimensionMeta);

    this.selectedOtherSearch.resultsFilter = (results) =>
      results.filter((variety) => variety.id !== this.id());

    if (APP_CONFIG.CROP_VARIETY_REQUIRED_ORIGIN) {
      this.origin = this.origin.extend({ required: true });
    }

    if (data) {
      this.crop(data.crop_id);
      this.cropEditable(!!data.crop_editable);
      this.origin(data.origin);
      this.origin2(data.origin_2);
      this.varietyType(data.variety_type);
      this.originType = data.origin_type;
      this.origin2Type = data.origin_2_type;
      this.parent1(data.parent_1);
      this.parent2(data.parent_2);
      this.replacing((data.replacing ?? []).slice());
      this.replacedBy(data.replaced_by ?? null);
    }
  }

  toData(): CropVarietyData {
    return {
      crop_id: this.crop(),
      variety_type: this.varietyType(),
      origin: this.origin(),
      origin_2: this.origin2(),
      parent_1: this.parent1(),
      parent_2: this.parent2(),
      ...super.toData(),
    };
  }
}

